import React, { useState, useEffect } from 'react';
import { FaTimes, FaChevronDown, FaCheckCircle } from 'react-icons/fa';
import { Employee, BookingSession, SendBookingRequestCommand, Address } from '../../services/api/types.ts';
import CalendarView from './CalendarView.tsx';
import { useAddress } from '../../hooks/useAddress.ts';
import { useEmployees } from '../../hooks/useEmployees.ts';
import { useBookingSessions } from '../../hooks/useBookingSessions.ts';
import { useBookingRequest } from '../../hooks/useBookingRequest.ts';
import styles from './BookingModal.module.css';


interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  className?: string;
}

const BookingModal: React.FC<BookingModalProps> = ({ className = '', isOpen = false, onClose }) => {
  const [selectedAddress, setSelectedAddress] = useState<Address | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [selectedSession, setSelectedSession] = useState<BookingSession | null>(null);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [errors, setErrors] = useState<{ phone?: string; email?: string }>({});
  const [currentCalendarMonth, setCurrentCalendarMonth] = useState(new Date());

  // Хуки
  const { address: addresses, loading: addressLoading, error: addressError, loadAddress } = useAddress();
  const { employees, loading: employeeLoading, error: employeeError, loadEmployees } = useEmployees();
  const { sessions, loading: sessionLoading, error: sessionError, loadSessions } = useBookingSessions();
  const { loading: bookingRequestLoading, error: bookingRequestError, setError: setErrorBookingRequest, sendBookingRequest } = useBookingRequest();


  // Загружаем адреса при открытии модалки
  useEffect(() => {
    if (isOpen) {
      loadAddress();
    }
  }, [isOpen, loadAddress]);

  // Автоматически выбираем адрес, если он один
  useEffect(() => {
    if (addresses.length === 1 && !selectedAddress) {
      setSelectedAddress(addresses[0]);
    }
  }, [addresses, selectedAddress]);

  // Загружаем сотрудников при выборе адреса
  useEffect(() => {
    if (selectedAddress) {
      loadEmployees(selectedAddress.Id);
    } else {
      setSelectedEmployee(null); // Сбрасываем мастера при смене адреса
    }
  }, [selectedAddress, loadEmployees]);

  // Загружаем сессии при выборе мастера
  useEffect(() => {
    if (selectedEmployee) {
      loadSessions({
        EmployeeId: selectedEmployee.Id,
        Year: currentCalendarMonth.getFullYear(),
        Month: currentCalendarMonth.getMonth() + 1,
      });
    } else {
      setSelectedSession(null); // Сбрасываем сессию при смене мастера
    }
  }, [selectedEmployee, currentCalendarMonth, loadSessions]);
  useEffect(() => {
    if (selectedEmployee && !selectedSession) {
      setTimeout(() => scrollToStep('step-calendar'), 100);
    }
  }, [selectedEmployee]);
  useEffect(() => {
    if (selectedSession) {
      setTimeout(() => scrollToStep('step-contacts'), 100);
    }
  }, [selectedSession]);

  const scrollToStep = (stepId: string) => {
    const element = document.getElementById(stepId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  };

  const handleMonthChange = (newMonth: Date) => {
    setCurrentCalendarMonth(newMonth);
  };

  // Валидация формы
  const validateForm = () => {
    const newErrors: { phone?: string; email?: string } = {};
    if (!phone) newErrors.phone = 'Введите номер телефона';
    else if (!/^\+7 \d{3}\ \d{3}-\d{2}-\d{2}$/.test(phone))
      newErrors.phone = 'Неверный формат: +7 (XXX) XXX-XX-XX';
    if (email && !/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Неверный формат email';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Отправка заявки
  const handleSubmit = async () => {
    if (!validateForm() || !selectedSession) return;

    try {
      await sendBookingRequest({
        BookingSessionId: selectedSession.Id,
        Phone: phone,
        Email: email.trim() || undefined,
      });
      setSubmitSuccess(true);
      setTimeout(() => {
        onClose();
        setSubmitSuccess(false);
        setSelectedAddress(null);
        setSelectedEmployee(null);
        setSelectedSession(null);
        setPhone('');
        setEmail('');
        setErrors({});
        setErrorBookingRequest('');
      }, 3000);
    } catch (err) {
      console.error('Ошибка:', err);
    }
  };

  const closeModal = () => {
    onClose();
    setSubmitSuccess(false);
    setSelectedAddress(null);
    setSelectedEmployee(null);
    setSelectedSession(null);
    setPhone('');
    setEmail('');
    setErrors({});
    setErrorBookingRequest('');
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) closeModal();
  };

  return (
    <>
      {/* Модалка */}
      {isOpen && (
        <div className={styles.modalBackdrop} onClick={handleBackdropClick}>
          <div className={styles.modalContent}>
            {/* Заголовок */}
            <div className={styles.header}>
              <h2>Забронировать сеанс</h2>
              <button className={styles.closeButton} onClick={closeModal}>
                <FaTimes />
              </button>
            </div>

            {/* Успешная отправка */}
            {submitSuccess ? (
              <div className={styles.successBlock}>
                <div className={styles.successIcon}>✓</div>
                <p className={styles.successText}>
                  Заявка отправлена!
                </p>
                <p className={styles.successHint}>
                  Мы свяжемся с вами в ближайшее время…
                </p>
              </div>
            ) : (
              <>
                {/* Шаг 1: Выбор адреса */}
                {addresses.length > 1 && (
                  <div className={styles.step}>
                    <label className={styles.label}>Выберите адрес</label>
                    <div className={styles.selectWrapper}>
                      <div
                        className={`${styles.selected} ${!selectedAddress ? styles.placeholder : ''}`}
                        onClick={() => document.getElementById('addressList')?.classList.toggle(styles.open)}
                      >
                        {selectedAddress ? selectedAddress.FullAddress : 'Выберите адрес'}
                        <span className={styles.arrow}></span>
                      </div>
                      <ul id="addressList" className={styles.options}>
                        {addressLoading ? (
                          <li className={styles.noOptions}>Загрузка адресов...</li>
                        ) : addressError ? (
                          <li className={styles.noOptions}>{addressError}</li>
                        ) : addresses.length === 0 ? (
                          <li className={styles.noOptions}>Адреса не найдены</li>
                        ) : (
                          addresses.map((addr) => (
                            <li
                              key={addr.Id}
                              className={styles.option}
                              onClick={() => {
                                setSelectedAddress(addr);
                                setSelectedEmployee(null);
                                setSelectedSession(null);
                                document.getElementById('addressList')?.classList.remove(styles.open);
                              }}
                            >
                              {addr.FullAddress}
                            </li>
                          ))
                        )}
                      </ul>
                    </div>
                  </div>
                )}

                {/* Шаг 2: Выбор мастера — только если выбран адрес */}
                {selectedAddress && (
                  <div className={styles.step}>
                    <label className={styles.label}>Выберите мастера</label>
                    <div className={styles.selectWrapper}>
                      <div
                        className={`${styles.selected} ${!selectedEmployee ? styles.placeholder : ''}`}
                        onClick={() => document.getElementById('employeeList')?.classList.toggle(styles.open)}
                      >
                        {selectedEmployee
                          ? `${selectedEmployee.FirstName} ${selectedEmployee.LastName}`
                          : 'Выберите мастера'}
                        <span className={styles.arrow}></span>
                      </div>
                      <ul id="employeeList" className={styles.options}>
                        {employeeLoading ? (
                          <li className={styles.noOptions}>Загрузка мастеров...</li>
                        ) : employeeError ? (
                          <li className={styles.noOptions}>{employeeError}</li>
                        ) : employees.length === 0 ? (
                          <li className={styles.noOptions}>Мастера не найдены</li>
                        ) : (
                          employees.map((emp) => (
                            <li
                              key={emp.Id}
                              className={styles.option}
                              onClick={() => {
                                setSelectedEmployee(emp);
                                setSelectedSession(null);
                                document.getElementById('employeeList')?.classList.remove(styles.open);
                              }}
                            >
                              {emp.FirstName} {emp.LastName}
                            </li>
                          ))
                        )}
                      </ul>
                    </div>
                  </div>
                )}

                {/* Шаг 3: Календарь и сессии — только если выбран мастер */}
                {selectedEmployee && (
                  <div id="step-calendar" className={styles.step}>
                    <label className={styles.label}>Выберите дату и время</label>
                    <CalendarView
                      sessions={sessions}
                      onDayClick={() => {
                        setSelectedSession(null)
                        setTimeout(() => scrollToStep('selected-day'), 100);
                      }}
                      onSessionClick={(session) => {
                        setSelectedSession(session);
                      }}
                      onMonthChange={handleMonthChange}
                      selectedSessionId={selectedSession?.Id || null}
                    />
                    {sessionLoading && <p className={styles.loading}>Загрузка сессий...</p>}
                    {sessionError && <p className={styles.error}>{sessionError}</p>}
                  </div>
                )}

                {/* Шаг 4: Форма контактов — только если выбрана сессия */}
                {selectedSession && (
                  <div id="step-contacts" className={styles.step}>
                    <label className={styles.label}>Ваш телефон</label>
                    <input
                      type="tel"
                      placeholder="+7 (___) ___-__-__"
                      value={phone}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        let value = e.target.value.replace(/\D/g, '');
                        if (value.startsWith('8')) value = '7' + value.slice(1);
                        if (!value.startsWith('7')) value = '7' + value;
                        let formatted = '+7';
                        if (value.length > 1) formatted += ` ${value.slice(1, 4)}`;
                        if (value.length > 4) formatted += ` ${value.slice(4, 7)}`;
                        if (value.length > 7) formatted += `-${value.slice(7, 9)}`;
                        if (value.length > 9) formatted += `-${value.slice(9, 11)}`;

                        setPhone(formatted.slice(0, 18));
                      }}
                      className={`${styles.input} ${errors.phone ? styles.inputError : ''}`}
                    />
                    {errors.phone && <span className={styles.error}>{errors.phone}</span>}

                    <label className={styles.label} style={{ marginTop: '1rem' }}>Ваш email (НЕ ОБЯЗАТЕЛЕН)</label>
                    <input
                      type="email"
                      placeholder="example@domain.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
                    />
                    {errors.email && <span className={styles.error}>{errors.email}</span>}
                  </div>
                )}

                {bookingRequestError && <span className={styles.error}>{bookingRequestError}</span>}

                {/* Кнопка отправки */}
                {selectedSession && (
                  <div className={styles.submitWrapper}>
                    <button
                      className={styles.submitButton}
                      onClick={handleSubmit}
                      disabled={bookingRequestLoading}
                    >
                      {bookingRequestLoading ? 'Отправка...' : 'Забронировать'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div >
      )}
    </>
  );
};

export default BookingModal;
